import type { MapAggregate } from "../domain/types.ts";
import { renderAggregatePanel, type AggregateDetailCoverage, updateAggregatePanelCoverage } from "./aggregate-panel.ts";
import { element } from "./dom.ts";
import { closeIcon, compassIcon, copyIcon, playIcon } from "./icons.ts";
import {
  bindMapNavigation,
  type MapDestination,
  type OutwardTarget,
  outwardNavigationDetail,
  renderMapNavigation,
} from "./map-navigation.ts";

export { element } from "./dom.ts";

const HINT_KEY = "lexatlas-cg-hint-dismissed";
export type BreadcrumbItem = {
  readonly label: string;
  readonly target: OutwardTarget;
  readonly current: boolean;
};

export type Shell = {
  readonly graph: HTMLDivElement;
  readonly loading: HTMLDivElement;
  readonly loadingCard: HTMLDivElement;
  readonly searchInput: HTMLInputElement;
  readonly searchResults: HTMLDivElement;
  readonly playButton: HTMLButtonElement;
  readonly yearSlider: HTMLInputElement;
  readonly yearLabel: HTMLDivElement;
  readonly panel: HTMLElement;
  readonly coverageStatus: HTMLElement;
  readonly mapNavigationToggle: HTMLButtonElement;
  readonly mapNavigationDrawer: HTMLElement;
  readonly mapNavigation: HTMLElement;
  readonly liveRegion: HTMLDivElement;
  readonly setMapNavigationReady: (ready: boolean) => void;
  readonly setDestinations: (destinations: readonly MapDestination[]) => void;
  readonly setBreadcrumbs: (items: readonly BreadcrumbItem[]) => void;
  readonly showAggregateDetail: (aggregate: MapAggregate, onClose: () => void, coverage?: AggregateDetailCoverage) => void;
  readonly updateAggregateDetailCoverage: (aggregate: MapAggregate, coverage: AggregateDetailCoverage) => void;
};

export class AppMountError extends Error {
  constructor() {
    super("#app ontbreekt");
    this.name = "AppMountError";
  }
}

function actionButton(label: string, eventName: string, icon: SVGSVGElement): HTMLButtonElement {
  const button = element("button", { type: "button", class: "quiet-action" }, [icon, element("span", {}, [label])]);
  button.addEventListener("click", () => button.dispatchEvent(new CustomEvent(eventName, { bubbles: true })));
  return button;
}

function mountLoading(root: HTMLElement): { readonly loading: HTMLDivElement; readonly card: HTMLDivElement } {
  const prerendered = root.querySelector<HTMLDivElement>("#atlas-loading");
  const prerenderedCard = prerendered?.querySelector<HTMLDivElement>(".loading-copy") ?? null;
  if (prerendered !== null && prerenderedCard !== null) return { loading: prerendered, card: prerenderedCard };
  const cloud = element("div", { class: "skeleton-cloud", "aria-hidden": "true" }, [
    element("i"), element("i"), element("i"), element("i"),
  ]);
  const card = element("div", { class: "loading-copy" }, [
    element("strong", {}, ["Atlas wordt opgebouwd"]),
    element("span", {}, ["Juridische gebieden en citatiestromen laden…"]),
  ]);
  return { loading: element("div", { class: "loading", role: "status", "aria-live": "polite" }, [cloud, card]), card };
}

function mountMapKey(): HTMLElement {
  return element("details", { class: "chrome map-key" }, [
    element("summary", {}, ["Kaartlegenda"]),
    element("div", { class: "key-items" }, [
      element("span", {}, [element("i", { class: "key-cloud" }), "Citatiesamenhang"]),
      element("span", {}, [element("i", { class: "key-dust" }), "Uitspraak"]),
      element("span", {}, [element("i", { class: "key-flow" }), "Samengevoegde stroom tussen thema’s"]),
      element("span", {}, [element("i", { class: "key-selected" }), "Geselecteerd"]),
      element("p", {}, ["Kleur toont een gemeenschap op basis van citaties, niet het gerecht. Stromen zijn totalen per thema, geen afzonderlijke uitspraak-tot-uitspraakverbindingen."]),
    ]),
  ]);
}

function mountMapNavigation(root: HTMLElement): {
  readonly toggle: HTMLButtonElement;
  readonly drawer: HTMLElement;
  readonly nav: HTMLElement;
  readonly close: HTMLButtonElement;
} {
  const prerenderedToggle = root.querySelector<HTMLButtonElement>("#map-navigation-toggle");
  const prerenderedDrawer = root.querySelector<HTMLElement>("#map-navigation-drawer");
  const prerenderedNav = root.querySelector<HTMLElement>("#map-navigation");
  const prerenderedClose = prerenderedDrawer?.querySelector<HTMLButtonElement>(".map-navigation-close") ?? null;
  if (prerenderedToggle !== null && prerenderedDrawer !== null && prerenderedNav !== null && prerenderedClose !== null) {
    return { toggle: prerenderedToggle, drawer: prerenderedDrawer, nav: prerenderedNav, close: prerenderedClose };
  }
  const toggle = element("button", {
    id: "map-navigation-toggle", class: "chrome map-navigation-toggle", type: "button",
    "aria-controls": "map-navigation-drawer", "aria-expanded": "false",
  }, ["Kaartgebieden"]);
  toggle.disabled = true;
  const close = element("button", { type: "button", class: "map-navigation-close", "aria-label": "Kaartgebieden sluiten" }, ["Sluiten"]);
  const nav = element("nav", {
    id: "map-navigation", class: "map-navigation", "aria-label": "Kaartgebieden",
    "aria-busy": "true", "aria-disabled": "true", tabindex: "-1",
  });
  const drawer = element("section", {
    id: "map-navigation-drawer", class: "chrome map-navigation-drawer", "aria-labelledby": "map-navigation-heading", hidden: "",
  }, [
    element("header", {}, [element("h2", { id: "map-navigation-heading" }, ["Kaartgebieden"]), close]),
    nav,
  ]);
  return { toggle, drawer, nav, close };
}

function renderBreadcrumbs(nav: HTMLElement, items: readonly BreadcrumbItem[]): void {
  const list = element("ol");
  for (const item of items) {
    const attributes: Record<string, string> = { type: "button" };
    if (item.current) attributes["aria-current"] = "page";
    const button = element("button", attributes, [item.label]);
    button.addEventListener("click", () => button.dispatchEvent(new CustomEvent("atlas:navigate-outward", {
      bubbles: true,
      detail: outwardNavigationDetail(item.target),
    })));
    list.append(element("li", {}, [button]));
  }
  nav.replaceChildren(list);
}

export function mountShell(root: HTMLElement): Shell {
  const skip = root.querySelector<HTMLAnchorElement>('a.skip-link[href="#map-navigation-toggle"]')
    ?? element("a", { class: "skip-link", href: "#map-navigation-toggle" }, ["Ga naar kaartgebieden"]);
  const graph = element("div", { id: "graph", "aria-hidden": "true" });
  const mapMain = element("main", { id: "atlas", "aria-label": "Interactieve jurisprudentie-atlas" }, [graph]);
  const mountedLoading = mountLoading(root);
  const brand = element("header", { class: "chrome brand" }, [
    element("p", { class: "brand-kicker" }, ["LexAtlas"]),
    element("h1", {}, ["Jurisprudentie-atlas"]),
    element("p", { class: "scale-copy" }, ["Schaal: juridische gebieden. Labels zijn automatische samenvattingen uit citatiepatronen en abstracts, geen officiële taxonomie."]),
  ]);
  const searchInput = element("input", {
    id: "atlas-search", type: "search", placeholder: "ECLI, titel of gerecht", autocomplete: "off", spellcheck: "false",
    role: "combobox", "aria-label": "Zoek ECLI, titel of gerecht", "aria-controls": "search-results", "aria-expanded": "false", "aria-autocomplete": "list",
  });
  const searchResults = element("div", { id: "search-results", class: "search-results", role: "listbox" });
  const commands = element("section", { class: "chrome commands", "aria-label": "Zoeken en kaartacties" }, [
    element("div", { class: "search-wrap" }, [element("label", { for: "atlas-search" }, ["Zoek in de atlas"]), searchInput, searchResults]),
    element("div", { class: "command-actions" }, [actionButton("Kopieer beeld", "atlas:copy-view", copyIcon()), actionButton("Breng me ergens", "atlas:landmark", compassIcon())]),
  ]);
  const breadcrumbs = element("nav", { class: "chrome breadcrumbs", "aria-label": "Kruimelpad" });
  renderBreadcrumbs(breadcrumbs, [{ label: "Atlas", target: "overview", current: true }]);
  const coverageStatus = element("p", { class: "chrome coverage-status", hidden: "" });
  const mountedMapNavigation = mountMapNavigation(root);
  const mapNavigation = mountedMapNavigation.nav;
  if (mapNavigation.dataset["prerendered"] !== "true") renderMapNavigation(mapNavigation, []);
  bindMapNavigation(mapNavigation);
  const hintClose = element("button", { type: "button", "aria-label": "Bedieningsuitleg sluiten" }, [closeIcon()]);
  const hint = element("aside", { class: "chrome hint", "aria-label": "Bedieningsuitleg" }, [element("span", {}, ["Zoom, pan of kies een kaartgebied met het toetsenbord"]), hintClose]);
  if (localStorage.getItem(HINT_KEY) !== null) hint.hidden = true;
  hintClose.addEventListener("click", () => { hint.hidden = true; localStorage.setItem(HINT_KEY, "1"); });
  const playButton = element("button", { type: "button", "aria-label": "Afspelen — speel tijdlijn" }, [playIcon(), element("span", {}, ["Afspelen"])]);
  const yearSlider = element("input", { id: "atlas-year", type: "range", min: "1841", max: "2026", value: "2026", "aria-describedby": "timeline-status" });
  const yearLabel = element("div", { class: "year", id: "timeline-status" }, ["2026"]);
  const noFlowCopy = element("span", { class: "no-flow-copy", role: "status" }, ["Geen citatiestromen in 1841; kaartgebieden blijven beschikbaar."]);
  const timebar = element("section", { class: "chrome timebar", "aria-label": "Tijdlijn" }, [playButton, element("label", { for: "atlas-year" }, ["Jaar"]), yearSlider, yearLabel, noFlowCopy]);
  const panel = element("aside", { class: "chrome panel", "aria-label": "Details", tabindex: "-1" });
  const liveRegion = element("div", { class: "sr-only", role: "status", "aria-live": "polite", "aria-atomic": "true", "data-live-region": "" });
  if (!skip.isConnected) root.prepend(skip);
  root.append(mapMain, brand, commands, breadcrumbs, coverageStatus, mountMapKey(), hint, timebar, panel, liveRegion);
  if (!mountedLoading.loading.isConnected) root.append(mountedLoading.loading);
  if (!mountedMapNavigation.toggle.isConnected) root.append(mountedMapNavigation.toggle);
  if (!mountedMapNavigation.drawer.isConnected) root.append(mountedMapNavigation.drawer);
  const closeMapNavigation = (restoreFocus = mountedMapNavigation.drawer.contains(document.activeElement)): void => {
    mountedMapNavigation.drawer.hidden = true;
    mountedMapNavigation.toggle.setAttribute("aria-expanded", "false");
    if (restoreFocus) mountedMapNavigation.toggle.focus();
  };
  const openMapNavigation = (): void => {
    if (mountedMapNavigation.toggle.disabled) return;
    mountedMapNavigation.drawer.hidden = false;
    mountedMapNavigation.toggle.setAttribute("aria-expanded", "true");
    mapNavigation.querySelector<HTMLButtonElement>('button[data-destination-id][tabindex="0"]')?.focus();
  };
  mountedMapNavigation.toggle.addEventListener("click", () => {
    if (mountedMapNavigation.drawer.hidden) openMapNavigation();
    else closeMapNavigation(true);
  });
  mountedMapNavigation.close.addEventListener("click", () => closeMapNavigation(true));
  mountedMapNavigation.drawer.addEventListener("keydown", (event) => {
    if (!(event instanceof KeyboardEvent) || event.key !== "Escape" || event.defaultPrevented) return;
    event.preventDefault();
    closeMapNavigation(true);
  });
  mapNavigation.addEventListener("atlas:close-navigation", () => closeMapNavigation(true));
  mapNavigation.addEventListener("atlas:destination", () => closeMapNavigation());
  skip.addEventListener("click", (event) => {
    event.preventDefault();
    openMapNavigation();
  });
  root.addEventListener("atlas:copy-view", () => { root.dataset["lastAction"] = "copy-view"; liveRegion.textContent = "Kaartlink klaar om te kopiëren"; });
  root.addEventListener("atlas:landmark", () => { root.dataset["lastAction"] = "landmark"; liveRegion.textContent = "Volgend herkenningspunt wordt geopend"; });
  root.addEventListener("atlas:motion-disabled", () => { liveRegion.textContent = "Automatisch afspelen staat uit bij verminderde beweging"; });
  const shell = {
    graph,
    loading: mountedLoading.loading,
    loadingCard: mountedLoading.card,
    searchInput,
    searchResults,
    playButton,
    yearSlider,
    yearLabel,
    panel,
    coverageStatus,
    mapNavigationToggle: mountedMapNavigation.toggle,
    mapNavigationDrawer: mountedMapNavigation.drawer,
    mapNavigation,
    liveRegion,
    setMapNavigationReady: (ready: boolean) => {
      mapNavigation.setAttribute("aria-busy", String(!ready));
      mapNavigation.setAttribute("aria-disabled", String(!ready));
      mountedMapNavigation.toggle.disabled = !ready;
      if (!ready) closeMapNavigation();
    },
    setDestinations: (destinations: readonly MapDestination[]) => renderMapNavigation(mapNavigation, destinations),
    setBreadcrumbs: (items: readonly BreadcrumbItem[]) => renderBreadcrumbs(breadcrumbs, items),
    showAggregateDetail: (aggregate: MapAggregate, onClose: () => void, coverage?: AggregateDetailCoverage) => renderAggregatePanel({ panel, aggregate, onClose, ...(coverage === undefined ? {} : { coverage }) }),
    updateAggregateDetailCoverage: (aggregate: MapAggregate, coverage: AggregateDetailCoverage) => updateAggregatePanelCoverage(panel, aggregate, coverage),
  } satisfies Shell;
  Object.defineProperty(window, "__atlasShell", { configurable: true, value: shell });
  return shell;
}

export function commitShellDestination(shell: Pick<Shell, "mapNavigation" | "liveRegion">, destination: MapDestination): void {
  const target = [...shell.mapNavigation.querySelectorAll<HTMLButtonElement>("button[data-destination-id]")]
    .find((button) => button.dataset["destinationId"] === destination.id) ?? null;
  if (target === null) return;
  shell.mapNavigation.querySelectorAll("button").forEach((button) => button.removeAttribute("aria-current"));
  target.setAttribute("aria-current", "true");
  const root = shell.mapNavigation.closest<HTMLElement>("#app");
  if (root !== null) root.dataset["level"] = "macro";
  shell.liveRegion.textContent = `${target.textContent ?? "Kaartgebied"} geopend`;
}

export function announce(shell: Shell, message: string): void {
  shell.liveRegion.textContent = message;
}

export function showShellError(shell: Shell, error: Error): void {
  shell.loading.classList.remove("hidden");
  shell.loading.dataset["state"] = "error";
  const retry = element("button", { type: "button", "data-action": "retry" }, ["Probeer opnieuw"]);
  retry.addEventListener("click", () => retry.dispatchEvent(new CustomEvent("atlas:retry", { bubbles: true })));
  shell.loadingCard.replaceChildren(
    element("strong", {}, ["Atlas kon niet laden"]),
    element("span", {}, [error.message]),
    retry,
  );
  announce(shell, "Atlas kon niet laden. Probeer opnieuw.");
}
